import { useMarketplacePreviewItems } from '@/apis/marketplace-preview/hooks/useMarketplacePreviewItems'
import { useMarketplacePreviewStoreById } from '@/apis/marketplace-preview/hooks/useMarketplacePreviewStoreById'
import { MarketplaceAccessPageProps } from '@/apis/marketplace-preview/types'
import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader'
import { usePagination } from '@/common/hooks/usePagination.tsx'
import { MarketplaceStore } from '@/queries/marketplace/schema'
import { AppRoutePaths } from '@/routing/route_paths'
import { useDebounce } from '@uidotdev/usehooks'
import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ProviderItems from '../../marketplace-live/providers/ProviderItems'

type MarketplaceStoreLocationState = {
  store?: MarketplaceStore
}

export default function MarketplacePreviewProviderDetailsPage({
  mode = 'preview',
}: MarketplaceAccessPageProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { providerId } = useParams()

  const marketplacePaths =
    mode === 'published'
      ? AppRoutePaths.marketplace_view
      : AppRoutePaths.marketplace_preview

  const { currentPage, perPage, setCurrentPage, setPerPage } = usePagination()

  const [itemSearch, setItemSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<string>('')

  const debouncedItemSearch = useDebounce(itemSearch, 500)
  const parsedProviderId = Number(providerId)

  const isValidProviderId =
    Number.isSafeInteger(parsedProviderId) && parsedProviderId > 0

  const storeFromNavigation = (
    location.state as MarketplaceStoreLocationState | null
  )?.store

  const itemsQuery = useMarketplacePreviewItems(
    {
      perPage,
      page: currentPage,
      search: debouncedItemSearch,
      business_type: selectedFilter,
      provider_id: isValidProviderId ? parsedProviderId : undefined,
    },
    mode,
    {
      enabled: isValidProviderId,
    }
  )

  // Falls back to looking the store up through the stores endpoint when it does
  // not come from the navigation state, e.g. on a reload or a direct link.
  const storeQuery = useMarketplacePreviewStoreById(parsedProviderId, mode, {
    enabled: isValidProviderId && !storeFromNavigation,
  })

  const store = storeFromNavigation ?? storeQuery.data

  const handleSetSelectedFilter = (filterValue: string) => {
    setSelectedFilter(filterValue)
    setCurrentPage(1)
  }

  const handleSearchChange = (search: string) => {
    setItemSearch(search)
    setCurrentPage(1)
  }

  function handleShopClick(item: MarketplaceItem) {
    navigate(marketplacePaths.details.replace(':itemId', item.id.toString()), {
      state: { item },
    })
  }

  if (!isValidProviderId) {
    return (
      <div className="text-center text-gray-500 py-10">Store not found.</div>
    )
  }

  // The store lookup query stays disabled (and therefore pending) when the store
  // already came from the navigation state, so it must be left out of the loader.
  const queries = storeFromNavigation ? [itemsQuery] : [itemsQuery, storeQuery]

  return (
    <MultiQueryPageLoader queries={queries}>
      {() => {
        if (!store) {
          return (
            <div className="text-center text-gray-500 py-10">
              Store not found.
            </div>
          )
        }

        return (
          <ProviderItems
            currentPage={currentPage}
            provider={store}
            perPage={perPage}
            totalRecords={itemsQuery.data?.total ?? 0}
            setCurrentPage={setCurrentPage}
            onItemClick={handleShopClick}
            setPerPage={setPerPage}
            search={itemSearch}
            setSearch={handleSearchChange}
            setSelectedFilter={handleSetSelectedFilter}
            items={itemsQuery.data?.data ?? []}
          />
        )
      }}
    </MultiQueryPageLoader>
  )
}
